/**
 * Pinning a frame to the wall's Pinned rows (Part 07 decision 7): the one
 * writer of `metadata.pinned`, which the membership rule reads here
 * (`lib/wall-membership.ts`) and on the server; the old canvas reads it too
 * (the ruler's top, the pinned page), so a pin means one thing everywhere.
 *
 * `canPinFrame` decides both whether a Pin control shows (the wall's pill,
 * the frame menu, the multi-selection menu) and whether a pin is written.
 * Another project's card in Everything (`foreignWallFrame`) is written
 * through its own project and can only be unpinned there: that project's
 * Pinned rows are not loaded here, and the server does not cap pins.
 */
import * as spacesApi from "@/api/spaces";
import { translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";
import type { FrameData } from "@/lib/frame-data";
import { PINNABLE_FRAME_TYPES } from "@/lib/frame-classification";
import { changeFrameMetadata } from "@/lib/frame-detail-requests";
import {
  isFramePinned,
  pinSlots,
  wallPlacement,
  type WallContext,
  type WallMember,
} from "@/lib/wall-membership";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { selectWallContext } from "@/stores/wall-feed-store";
import { foreignWallFrame, useWallScopeStore } from "@/stores/wall-scope-store";

/**
 * Whether Pin (or Unpin) applies to `frame`.
 *
 * - Not pinned: a gallery card of a pinnable kind can be pinned.
 * - Pinned: the user's pin can always be taken back, whether it shows in
 *   the Pinned rows, waits beyond their cap in the gallery, or is archived.
 * - Never: the Project's tools (task board, memory) and kinds that never
 *   show on the wall.
 */
export function canPinFrame(frame: WallMember, ctx: WallContext): boolean {
  const placement = wallPlacement(frame, ctx);
  if (!isFramePinned(frame)) {
    return placement === "wall" && PINNABLE_FRAME_TYPES.has(frame.frameType);
  }
  if (placement === "wall") return true;
  if (placement === "tools") return ctx.pinnedTools.includes(frame.frameId);
  return (
    Boolean(frame.archivedAt) &&
    wallPlacement({ ...frame, archivedAt: null }, ctx) === "wall"
  );
}

/** Another project's card: Unpin only (its project's room is unknown here). */
function canPinForeignFrame(frame: WallMember): boolean {
  return isFramePinned(frame);
}

/**
 * `canPinFrame` for a loaded frame, or `canPinForeignFrame` for another
 * project's card, re-rendering only when it changes.
 */
export function useCanPinFrame(frameId: string): boolean {
  const own = useFramesStore((state) => {
    const frame = state.framesById[frameId];
    return frame ? canPinFrame(frame, selectWallContext(state)) : null;
  });
  const foreign = useWallScopeStore((state) => {
    const frame = state.foreignFramesById[frameId];
    return !!frame && canPinForeignFrame(frame);
  });
  return own ?? foreign;
}

/** Whether a loaded frame (or a foreign card) is pinned, re-rendering only when that changes. */
export function useFramePinned(frameId: string): boolean {
  const own = useFramesStore((state) => {
    const frame = state.framesById[frameId];
    return frame ? isFramePinned(frame) : null;
  });
  const foreign = useWallScopeStore((state) => {
    const frame = state.foreignFramesById[frameId];
    return !!frame && isFramePinned(frame);
  });
  return own ?? foreign;
}

function pinnedRowsHaveRoom(ctx: WallContext): boolean {
  return ctx.pinnedTools.length < pinSlots(ctx.canonicalSingletons);
}

type PinOutcome = "written" | "full" | "skipped";

/**
 * Save a foreign card's pin through its project as a `metadata_patch` (the
 * card has only the listing's keys; the server sets `pinned` over the
 * stored metadata under its row lock), then the card in Everything's map
 * once the server agrees.
 */
async function saveForeignPin(
  frame: FrameData,
  projectId: string,
  pinned: boolean,
): Promise<void> {
  try {
    await spacesApi.updateFrame(projectId, frame.frameId, {
      metadata_patch: { pinned },
    });
    const shown = useWallScopeStore.getState().foreignFramesById[frame.frameId];
    if (!shown) return;
    useWallScopeStore.getState().patchForeignFrame(frame.frameId, {
      metadata: { ...shown.metadata, pinned },
    });
  } catch {
    useNotificationStore.getState().addSystemToast({
      id: `wall-pin-failed-${frame.frameId}`,
      title: translate(
        libNoticesMessages,
        pinned ? "pinFailed" : "unpinFailed",
      ),
      description: translate(framesSharedMessages, "checkConnection"),
      level: "error",
    });
  }
}

/** Writes the pin when it applies and fits; never notifies. */
function writePin(frameId: string, pinned: boolean): PinOutcome {
  const foreign = foreignWallFrame(frameId);
  if (foreign) {
    if (
      isFramePinned(foreign) === pinned ||
      !canPinForeignFrame(foreign) ||
      !foreign.spaceId
    ) {
      return "skipped";
    }
    void saveForeignPin(foreign, foreign.spaceId, pinned);
    return "written";
  }
  const state = useFramesStore.getState();
  const frame = state.framesById[frameId];
  if (!frame || isFramePinned(frame) === pinned) return "skipped";
  const ctx = selectWallContext(state);
  if (!canPinFrame(frame, ctx)) return "skipped";
  if (pinned && !pinnedRowsHaveRoom(ctx)) return "full";
  changeFrameMetadata(
    frameId,
    (metadata) => ({ ...metadata, pinned }),
    (metadata) => {
      const { currentProject, currentSpace } = useProjectStore.getState();
      if (!currentProject || !currentSpace) return;
      void spacesApi
        .updateFrame(currentProject.id, currentSpace.id, frameId, { metadata })
        .catch((err) =>
          console.error("[frame-pinning] Backend update failed:", err),
        );
    },
  );
  return "written";
}

function notifyPinnedFull(): void {
  useNotificationStore.getState().addSystemToast({
    id: "wall-pinned-full",
    title: translate(libNoticesMessages, "pinnedFull"),
    level: "info",
  });
}

/**
 * Pin or unpin `frameId`: the frames store at once, then the merged
 * metadata on the server (`changeFrameMetadata`). A pin while the rows are
 * full writes nothing and says so; an unpin never checks.
 */
export function setFramePinned(frameId: string, pinned: boolean): void {
  if (writePin(frameId, pinned) === "full") notifyPinnedFull();
}

/** Pin each of `frameIds` that can be pinned, until the rows are full (one notice). */
export function pinFrames(frameIds: readonly string[]): void {
  for (const frameId of frameIds) {
    if (writePin(frameId, true) === "full") {
      notifyPinnedFull();
      return;
    }
  }
}
